import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import Grid from '@mui/material/Grid';
import { makeStyles } from '@mui/styles';
import { Theme } from '@mui/material/styles';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import LaunchIcon from '@mui/icons-material/Launch';
import Divider from '@mui/material/Divider';
import useLanguage from '../../../../components/_hook/useLanguage';
import useNotification from '../../../../components/_hook/useNotification';
import useRouter from '../../../../components/_hook/useRouter';
import { useProductQuery } from '../../../../../graphql/query/product/useProductQuery';
import NotFound from '../../../../components/_error/NotFound';
import Context from '../../../../context';
import useRoutes from '../../../../components/_hook/useRoutes';
import ProductLayout from '../../../../layout/Product';
import useKeepitalRoutes from '../../../../components/_hook/useKeepitalRoutes';
import Helmet from '../../../../components/Helmet';
import ProductImageUpload from '../../../../components/_upload/ProductImageUpload';
import ProductCatalogueUpload from '../../../../components/_upload/ProductCatalogueUpload';
import ProductDetailDropshippedProductInfo from '../_module/ProductDetailDropshippedProductInfo';

const useStyles = makeStyles<Theme>((theme: Theme) => ({
  paper: {
    margin: theme.spacing(1),
    padding: theme.spacing(3),
    width: '100%',
    paddingBottom: theme.spacing(10),
  },
}));

function ProductDetailMedia() {
  const { t } = useTranslation();
  const classes = useStyles();
  const { notification } = useNotification();
  const { params, history } = useRouter();
  const { path } = useRoutes();
  const { company } = useContext(Context);
  const { docID: productId } = params;
  const { getText } = useLanguage();
  const { path: keepitalPath } = useKeepitalRoutes();
  const productFragment = `
            _id
            status
            slug
            featured
            name {
              la
              text
            }
            isDropshippedProduct
            media {
              images {
                  status 
                  filename
                  filename_large
  
                  filenameUrl
                  filenameLargeUrl
              }
              attachments {
                  status 
                  filename
                  
                  filenameUrl
              }
            }
  `;
  const { data, loading } = useProductQuery(productFragment, {
    variables: {
      filter: {
        companyId: company._id,
        _id: productId as string,
      },
    },
    skip: productId === 'new',
  });

  if (productId !== 'new' && loading && productId) {
    return (
      <Grid container justifyContent="center">
        <CircularProgress size={30} color="inherit" />
      </Grid>
    );
  }

  const product = productId && data ? data.products.items[0] : null;

  if (!product) {
    return <NotFound />;
  }

  if (product && product.isDropshippedProduct) {
    return <ProductDetailDropshippedProductInfo />;
  }

  return (
    <>
      <Paper square className={classes.paper}>
        <Helmet title={`${t('media')} | ${t('product')}`} />
        <Grid container spacing={2}>
          <Grid container justifyContent="space-between" spacing={1}>
            <Grid item>
              <Typography variant="h6">
                {t('update')} {getText(product.name)} {t('media')}
              </Typography>
            </Grid>
            {product && (
              <Grid item>
                <Button
                  size="small"
                  color="primary"
                  variant="contained"
                  startIcon={<LaunchIcon />}
                  component="a"
                  href={keepitalPath('company.showcase.product.index', {
                    origin: company.origin ? company.origin.toLowerCase() : '',
                    companySlug: company.slug ? company.slug : '',
                    productSlug: product.slug,
                  })}
                  target="_blank"
                >
                  {t('view')}
                </Button>
              </Grid>
            )}
            <Grid item xs={12}>
              <Divider />
            </Grid>
          </Grid>
          <Grid container item xs={12} spacing={3}>
            <Grid item xs={12}>
              <ProductImageUpload
                productId={product._id}
                images={
                  product.media && product.media.images && product.media.images
                    ? product.media.images
                    : []
                }
              />
            </Grid>
            <Grid item xs={12}>
              <ProductCatalogueUpload
                productId={product._id}
                catalogues={
                  product.media &&
                  product.media.attachments &&
                  product.media.attachments
                    ? product.media.attachments
                    : []
                }
              />
            </Grid>
          </Grid>
        </Grid>
      </Paper>
    </>
  );
}

export default () => <ProductLayout main={<ProductDetailMedia />} />;
